import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { useToast } from '@/components/ui/Toast'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { MoneyText } from '@/components/ui/MoneyText'
import { Sheet } from '@/components/ui/Sheet'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { Textarea } from '@/components/ui/Textarea'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { useProfile } from '@/auth/SessionProvider'
import { canWriteDepartment } from '@/auth/profile'
import { downloadCsv } from '@/lib/csv'
import { formatDay, formatMonth, shiftMonth } from '@/lib/format'
import { formatKwacha, toInputText } from '@/lib/money'
import { lusakaToday } from '@/lib/periods'
import { listPayments, METHOD_LABELS, reversePayment } from '@/data/dues'
import type { Payment, PaymentMethod } from '@/data/dues'

const QUICK_REASONS = ['Wrong amount', 'Wrong person', 'Entered twice']

/** Money in, one month at a time, with totals and a way to cancel a mistake. */
export function PaymentsPage({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const qc = useQueryClient()
  const canWrite = canWriteDepartment(profile, deptId)
  const thisMonth = lusakaToday().slice(0, 7)
  const [month, setMonth] = useState(thisMonth)
  const [target, setTarget] = useState<Payment | null>(null)
  const [reason, setReason] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const toast = useToast()

  const q = useQuery({ queryKey: ['payments', deptId, month], queryFn: () => listPayments({ departmentId: deptId, month, limit: 500 }) })

  const totals = useMemo(() => {
    // Every row counts: an original payment stays on the books and its reversal is
    // a matching NEGATIVE row, so the sum of all rows is the true net figure.
    const byMethod: Partial<Record<PaymentMethod, number>> = {}
    let net = 0
    for (const p of q.data ?? []) {
      if (p.status !== 'posted' && p.status !== 'reversed') continue
      net += p.amountNgwee
      byMethod[p.method] = (byMethod[p.method] ?? 0) + p.amountNgwee
    }
    return { net, byMethod }
  }, [q.data])

  const reverse = useMutation({
    mutationFn: () => reversePayment((target as Payment).id, reason),
    onSuccess: () => {
      toast.success(`Cancelled ${formatKwacha((target as Payment).amountNgwee)} from ${(target as Payment).memberName}. Both entries stay on record.`)
      setTarget(null)
      setReason('')
      setFormError(null)
      void qc.invalidateQueries({ queryKey: ['payments', deptId] })
      void qc.invalidateQueries({ queryKey: ['members', deptId] })
      void qc.invalidateQueries({ queryKey: ['ledger', deptId] })
      void qc.invalidateQueries({ queryKey: ['arrears', deptId] })
    },
    onError: (e) => setFormError(e instanceof Error ? e.message : 'Could not cancel the payment.'),
  })

  function confirmReverse() {
    if (reverse.isPending) return
    if (reason.trim().length < 3) return setFormError('Say briefly why (at least 3 letters).')
    setFormError(null)
    reverse.mutate()
  }

  function exportCsv() {
    const rows = (q.data ?? []).map((p) => [
      p.receivedOn,
      p.memberName,
      METHOD_LABELS[p.method],
      Number(toInputText(p.amountNgwee)),
      p.status === 'reversed' ? 'Cancelled' : p.reversesId ? 'Reversal' : 'Recorded',
      p.reference ?? '',
      p.note ?? '',
    ])
    downloadCsv(`payments-${month}.csv`, [['Date', 'Member', 'Method', 'Amount (Kwacha)', 'Status', 'Reference', 'Note'], ...rows])
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-title font-semibold text-ink">Payments</h2>
        {canWrite && (
          <Link to="/d/$deptId/pay" params={{ deptId }} className="inline-flex items-center justify-center rounded-control bg-brand px-4 py-2.5 min-h-[44px] font-medium text-white hover:bg-brand-hover">
            Record payment
          </Link>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 rounded-card border border-line bg-surface p-1.5">
        <Button variant="ghost" size="sm" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>
          <ChevronLeft className="size-5" aria-hidden="true" />
        </Button>
        <span className="font-medium text-ink" aria-live="polite">
          {formatMonth(month)}
        </span>
        <Button variant="ghost" size="sm" aria-label="Next month" disabled={month >= thisMonth} onClick={() => setMonth(shiftMonth(month, 1))}>
          <ChevronRight className="size-5" aria-hidden="true" />
        </Button>
      </div>

      {q.isPending ? (
        <SkeletonRows rows={5} label="Loading payments" />
      ) : q.isError ? (
        <Card>
          <CardBody>
            <p role="alert" className="text-body text-bad">
              {q.error instanceof Error ? q.error.message : 'Could not load the payments.'}
            </p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void q.refetch()}>
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : q.data.length === 0 ? (
        <EmptyState title={`No payments in ${formatMonth(month)}`} description="Payments you record will appear here." />
      ) : (
        <>
          <Card>
            <CardBody>
              <p className="text-body text-muted">Net collected in {formatMonth(month)}</p>
              <p className="text-micro text-muted">After cancellations. A cancellation counts in the month it was made.</p>
              <p className="text-title font-semibold text-ink mt-1">
                <MoneyText ngwee={totals.net} />
              </p>
              <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-body text-muted">
                {(Object.keys(totals.byMethod) as PaymentMethod[]).map((m) => (
                  <li key={m}>
                    {METHOD_LABELS[m]}: <span className="font-medium text-ink">{formatKwacha(totals.byMethod[m] ?? 0)}</span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          {q.data.length >= 500 && (
            <p role="status" className="rounded-control border border-wait/40 bg-wait-tint p-3 text-body text-wait">
              Showing the latest 500 payments. The total above may be missing older ones this month.
            </p>
          )}
          <ul className="flex flex-col gap-2" aria-label="Payments">
            {q.data.map((p) => {
              const isReversal = p.reversesId !== null
              const cancelled = p.status === 'reversed'
              return (
                <li key={p.id} className="rounded-card border border-line bg-surface p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={`font-medium truncate ${cancelled ? 'text-muted line-through' : 'text-ink'}`}>{p.memberName}</p>
                      <p className="text-body text-muted">
                        {formatDay(p.receivedOn)} · {METHOD_LABELS[p.method]}
                        {p.reference ? ` · ${p.reference}` : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`font-semibold ${cancelled ? 'text-muted line-through' : p.amountNgwee < 0 ? 'text-bad' : 'text-ink'}`}>
                        {formatKwacha(p.amountNgwee)}
                      </p>
                      {cancelled && <Badge tone="neutral">Cancelled</Badge>}
                      {isReversal && <Badge tone="warning">Reversal</Badge>}
                    </div>
                  </div>
                  {p.note && <p className="mt-1 text-body text-muted">{p.note}</p>}
                  {!isReversal && p.amountNgwee > 0 && (
                    <OwnerDeleteButton table="contribution" id={p.id} what="payment" wrap="mt-2 flex flex-wrap gap-2" />
                  )}
                  {canWrite && p.status === 'posted' && !isReversal && (
                    <button
                      type="button"
                      onClick={() => {
                        setTarget(p)
                        setReason('')
                        setFormError(null)
                      }}
                      className="mt-2 min-h-[44px] text-body text-muted underline"
                    >
                      Cancel this payment
                    </button>
                  )}
                </li>
              )
            })}
          </ul>

          <div>
            <Button variant="secondary" size="sm" onClick={exportCsv}>
              Download for Excel
            </Button>
          </div>
        </>
      )}

      <Sheet isOpen={target !== null} onClose={() => setTarget(null)} title="Cancel this payment?">
        {target && (
          <div className="flex flex-col gap-4 p-4">
            <p className="text-neutral">
              {formatKwacha(target.amountNgwee)} from <strong>{target.memberName}</strong> on {formatDay(target.receivedOn)}.
            </p>
            <p className="text-body text-muted">
              The payment is not deleted. A matching cancellation is added, so the history stays honest, and the dues it paid become unpaid again.
            </p>
            <div className="flex flex-wrap gap-2" aria-label="Common reasons">
              {QUICK_REASONS.map((r) => (
                <Button key={r} type="button" variant="secondary" size="sm" onClick={() => setReason(r)}>
                  {r}
                </Button>
              ))}
            </div>
            <Textarea label="Why are you cancelling it?" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
            {formError && (
              <p role="alert" className="text-body text-bad">
                {formError}
              </p>
            )}
            <div className="flex gap-3">
              <Button variant="danger" size="lg" className="flex-1" isLoading={reverse.isPending} onClick={confirmReverse}>
                Cancel payment
              </Button>
              <Button variant="secondary" size="lg" onClick={() => setTarget(null)}>
                Keep it
              </Button>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  )
}
